// PALMEX · Produktseite
// <px-versandbalken>: „Noch X € bis zum kostenlosen Versand“ als Fortschrittsbalken über dem Kaufen-Knopf.
// Rechnet Warenkorb + gewählte Variante × Menge (ist das Produkt schon im Warenkorb, nur den Warenkorb).
// Hört auf Variantenwechsel, Mengenänderung und Warenkorb-Ereignisse (shopify:cart:lines-update) und lädt
// danach den echten Warenkorb neu. Portiert aus dem Theme von Total EquiPro (tep-produktseite.js).
import { StandardEvents } from '@shopify/events';

/** Cent-Betrag im Geldformat des Shops (z. B. „{{amount_with_comma_separator}}€“) */
export function geld(cent, format) {
  const wert = (Number(cent) || 0) / 100;
  const zahl = (stellen, tausend, dezimal) => {
    const [ganz, rest] = wert.toFixed(stellen).split('.');
    return ganz.replace(/\B(?=(\d{3})+(?!\d))/g, tausend) + (rest ? dezimal + rest : '');
  };
  const arten = {
    amount: () => zahl(2, ',', '.'),
    amount_no_decimals: () => zahl(0, ',', '.'),
    amount_with_comma_separator: () => zahl(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => zahl(0, '.', ','),
    amount_with_apostrophe_separator: () => zahl(2, "'", '.'),
  };
  return (format || '{{amount_with_comma_separator}} €').replace(/\{\{\s*(\w+)\s*\}\}/, (_, art) =>
    (arten[art] || arten.amount_with_comma_separator)()
  );
}

const json = (el, auswahl) => {
  try {
    return JSON.parse(el.querySelector(auswahl)?.textContent || '{}');
  } catch {
    return {};
  }
};

const formular = (abschnitt) => abschnitt?.querySelector('form[data-type="add-to-cart-form"]');
const menge = (abschnitt) => Number(abschnitt?.querySelector('product-form-component input[name="quantity"]')?.value) || 1;

/** Nach einem Variantenwechsel erst lesen, wenn das Theme das versteckte Feld „id“ umgestellt hat */
const nachVariantenwechsel = (event, fn) =>
  (event.promise || Promise.resolve()).then(() => setTimeout(fn, 0)).catch(() => {});

class PxVersandbalken extends HTMLElement {
  connectedCallback() {
    this.abbruch = new AbortController();
    const { signal } = this.abbruch;
    this.abschnitt = this.closest('.shopify-section') || document;
    this.varianten = json(this, '[data-varianten]');
    this.grenze = Number(this.dataset.grenze) || 0;
    this.korb = Number(this.dataset.korb) || 0;
    this.imKorb = this.dataset.imKorb === 'true';

    this.abschnitt.addEventListener(StandardEvents.productSelect, (e) => nachVariantenwechsel(e, () => this.zeichnen()), { signal });
    this.abschnitt.addEventListener('input', (e) => e.target?.name === 'quantity' && this.zeichnen(), { signal });
    this.abschnitt.addEventListener('change', (e) => e.target?.name === 'quantity' && this.zeichnen(), { signal });
    document.addEventListener('quantity-selector:update', () => this.zeichnen(), { signal });
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (e) => (e.promise || Promise.resolve()).then(() => this.korbLaden()).catch(() => {}),
      { signal }
    );
    window.addEventListener('pageshow', (e) => e.persisted && this.korbLaden(), { signal });
    this.korbLaden();
  }

  disconnectedCallback() {
    this.abbruch?.abort();
  }

  async korbLaden() {
    try {
      const korb = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      this.korb = Number(korb.total_price) || 0;
      this.imKorb = korb.items.some((i) => String(i.product_id) === this.dataset.produkt);
    } catch {
      // ohne Antwort bleibt der Stand vom Seitenaufbau
    }
    this.zeichnen();
  }

  zeichnen() {
    const id = formular(this.abschnitt)?.querySelector('input[name="id"]')?.value || this.dataset.variante;
    const artikel = this.imKorb ? 0 : (this.varianten[id] ?? 0) * menge(this.abschnitt);
    const plan = this.korb + artikel;
    const fehlt = this.grenze - plan;
    const t = this.dataset;
    let vorlage;
    if (this.korb >= this.grenze) vorlage = t.textFrei;
    else if (fehlt <= 0) vorlage = artikel > 0 ? t.textFreiArtikel : t.textFrei;
    else vorlage = artikel > 0 ? t.textArtikel : t.textKorb;

    const text = this.querySelector('[data-text]');
    if (text) {
      text.replaceChildren();
      const [vor, nach] = String(vorlage || '').split('[betrag]');
      text.append(vor);
      if (nach !== undefined) {
        const fett = document.createElement('strong');
        fett.textContent = geld(fehlt, t.geld);
        text.append(fett, nach);
      }
    }
    const prozent = this.grenze > 0 ? Math.max(0, Math.min(100, Math.round((plan / this.grenze) * 100))) : 100;
    const balken = this.querySelector('[role="progressbar"]');
    balken?.setAttribute('aria-valuenow', String(prozent));
    this.style.setProperty('--px-fortschritt', `${prozent}%`);
    this.classList.toggle('px-versand--erreicht', fehlt <= 0);
  }
}

if (!customElements.get('px-versandbalken')) customElements.define('px-versandbalken', PxVersandbalken);

// <px-lieferung>: Lieferzeitraum als Datum („Lieferung ca. Di., 13.10. – Do., 15.10.“). Zählt Werktage Mo–Fr ab
// morgen und überspringt die bundesweiten Feiertage (Neujahr, Karfreitag, Ostermontag, 1. Mai, Christi Himmelfahrt,
// Pfingstmontag, 3. Oktober, 1. und 2. Weihnachtstag). Ohne Skript bleibt „Lieferzeit ca. 3–5 Werktage“ stehen.
const ostersonntag = (jahr) => {
  const a = jahr % 19, b = Math.floor(jahr / 100), c = jahr % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  return new Date(jahr, Math.floor(n / 31) - 1, (n % 31) + 1);
};
const feiertage = new Map();
const istFeiertag = (tag) => {
  const jahr = tag.getFullYear();
  if (!feiertage.has(jahr)) {
    const ostern = ostersonntag(jahr);
    const versetzt = (n) => {
      const x = new Date(ostern);
      x.setDate(x.getDate() + n);
      return `${x.getMonth() + 1}-${x.getDate()}`;
    };
    feiertage.set(jahr, new Set(['1-1', '5-1', '10-3', '12-25', '12-26', versetzt(-2), versetzt(1), versetzt(39), versetzt(50)]));
  }
  return feiertage.get(jahr).has(`${tag.getMonth() + 1}-${tag.getDate()}`);
};
const istWerktag = (tag) => tag.getDay() !== 0 && tag.getDay() !== 6 && !istFeiertag(tag);
const plusWerktage = (start, anzahl) => {
  const tag = new Date(start);
  for (let gezaehlt = 0; gezaehlt < anzahl; ) {
    tag.setDate(tag.getDate() + 1);
    if (istWerktag(tag)) gezaehlt++;
  }
  return tag;
};

class PxLieferung extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min);
    const max = Number(this.dataset.max);
    const ziel = this.querySelector('[data-datum]');
    if (!ziel || !(min > 0) || !(max >= min)) return;
    const heute = new Date();
    const format = new Intl.DateTimeFormat(document.documentElement.lang || 'de', { weekday: 'short', day: '2-digit', month: '2-digit' });
    const von = format.format(plusWerktage(heute, min));
    const bis = format.format(plusWerktage(heute, max));
    ziel.textContent = (this.dataset.vorlage || '[von] – [bis]').replace('[von]', von).replace('[bis]', min === max ? von : bis);
    if (min === max) ziel.textContent = ziel.textContent.replace(` – ${von}`, '');
    this.toggleAttribute('data-fertig', true);
  }
}

if (!customElements.get('px-lieferung')) customElements.define('px-lieferung', PxLieferung);

// Lupen-Cursor: Über den Produktbildern folgt ein runder „Zoom“-Knopf weich dem Mauszeiger (nur feiner Zeiger).
// Klick öffnet weiterhin Horizons Zoom-Ansicht. Die Galerie wird beim Variantenwechsel ersetzt, daher Delegation.
const lupeStarten = () => {
  if (document.querySelector('.px-lupe') || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (!document.querySelector('.product-information media-gallery')) return;
  const lupe = document.createElement('div');
  lupe.className = 'px-lupe';
  lupe.setAttribute('aria-hidden', 'true');
  lupe.textContent = 'Zoom';
  document.body.append(lupe);
  document.documentElement.classList.add('px-lupe-aktiv');
  const ruhig = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let x = 0, y = 0, zx = 0, zy = 0, laeuft = false;
  const schritt = () => {
    const f = ruhig ? 1 : 0.2;
    zx += (x - zx) * f;
    zy += (y - zy) * f;
    lupe.style.transform = `translate(${zx - 42}px, ${zy - 42}px)`;
    if (Math.abs(x - zx) > 0.3 || Math.abs(y - zy) > 0.3) requestAnimationFrame(schritt);
    else laeuft = false;
  };
  document.addEventListener(
    'pointermove',
    (e) => {
      const ueber = e.target instanceof Element && e.target.closest('.product-information media-gallery .product-media-container--zoomable');
      lupe.toggleAttribute('data-an', Boolean(ueber));
      x = e.clientX;
      y = e.clientY;
      if (!laeuft) {
        laeuft = true;
        requestAnimationFrame(schritt);
      }
    },
    { passive: true }
  );
  document.addEventListener('pointerleave', () => lupe.removeAttribute('data-an'));
};
lupeStarten();
